import { defineStore } from 'pinia';
import type { FontInfo, UploadedFont, GoogleFontTagGroupsData, RecentFont, FontGroup } from '@/types/library/font';

import { mergeUploadedFace } from '@/helpers/fontHelper';
import { get, set } from '@/utils/localStorage';
import { useAppStore } from '@/stores/app';

/**
 * Font store — holds data only.
 *
 * - `allGoogleFonts` / `loadedGoogleFonts`: the Google Fonts catalogue and the
 *   set of families whose files have been loaded into the browser.
 * - `uploadedFonts`: user-uploaded custom fonts, grouped by family (each
 *   sub-family is a variant inside the family's s3Url map).
 * - Every action here only changes state. No API calls, toasts, i18n, or
 *   reading other stores. The real work (fetching, validating, uploading,
 *   loading FontFaces, notifying) lives in services/font/fontLibraryService.
 */
export const useFontStore = defineStore('font', {
  /**
   * ═══════════════════════════════════════════════════════════════════
   * STATE
   * ═══════════════════════════════════════════════════════════════════
   */

  state: () => ({
    allGoogleFonts: new Map<string, FontInfo>(),
    loadedGoogleFonts: new Set<string>(),
    // Filter-menu universe (tag groups + subsets). Built once from the catalogue
    // at load time, never recomputed on per-font merges — see setTagGroupsData.
    tagGroupsData: { tagGroups: [], subsets: [] } as GoogleFontTagGroupsData,
    loading: false,
    initialized: false,
    uploadedFonts: new Map<string, FontInfo>(),
    uploadingFont: false,
    uploadedFontsInitialized: false,
    uploadedFontsLoading: false,
    recentFonts: [] as RecentFont[]
  }),

  /**
   * ═══════════════════════════════════════════════════════════════════
   * GETTERS
   * ═══════════════════════════════════════════════════════════════════
   */

  getters: {
    getAllGoogleFonts: state => state.allGoogleFonts,
    googleFontsArray: (state): FontInfo[] =>
      Array.from(state.allGoogleFonts.values()),
    getLoadedGoogleFonts: state => state.loadedGoogleFonts,
    getTagGroupsData: state => state.tagGroupsData,
    isLoading: state => state.loading,
    isInitialized: state => state.initialized,
    getUploadedFonts: state => state.uploadedFonts,
    uploadedFontsArray: (state): FontInfo[] =>
      Array.from(state.uploadedFonts.values()),
    isUploadingFont: state => state.uploadingFont,
    isUploadedFontsInitialized: state => state.uploadedFontsInitialized,
    isUploadedFontsLoading: state => state.uploadedFontsLoading,
    getRecentFonts: (state): RecentFont[] => state.recentFonts
  },

  /**
   * ═══════════════════════════════════════════════════════════════════
   * ACTIONS — simple state setters only
   * ═══════════════════════════════════════════════════════════════════
   */

  actions: {
    /* ── Google fonts ── */

    addGoogleFonts(fonts: FontInfo[]) {
      this.$patch(state => {
        for (const font of fonts) {
          state.allGoogleFonts.set(font.fontFamily, font);
        }
      });
    },

    setAllGoogleFonts(entries: [string, FontInfo][]) {
      this.allGoogleFonts = new Map(entries);
    },

    setGoogleFont(font: FontInfo) {
      this.allGoogleFonts.set(font.fontFamily, font);
    },

    markGoogleFontLoaded(fontFamily: string) {
      this.loadedGoogleFonts.add(fontFamily);
    },

    setTagGroupsData(data: GoogleFontTagGroupsData) {
      this.tagGroupsData = data;
    },

    setLoading(value: boolean) {
      this.loading = value;
    },

    setInitialized(value: boolean) {
      this.initialized = value;
    },

    /* ── Uploaded (custom) fonts ── */

    setUploadedFonts(fonts: Map<string, FontInfo>) {
      this.uploadedFonts = fonts;
    },

    mergeUploadedFace(face: UploadedFont) {
      mergeUploadedFace(this.uploadedFonts, face);
    },

    setUploadingFont(value: boolean) {
      this.uploadingFont = value;
    },

    setuploadedFontsInitialized(value: boolean) {
      this.uploadedFontsInitialized = value;
    },

    setuploadedFontsLoading(value: boolean) {
      this.uploadedFontsLoading = value;
    },
    getRecentFontsKey(): string | null {
      const appStore = useAppStore();
      const bpId = appStore.getBusinessProfileId;
      const userId = appStore.user?.id;
      if (!bpId || !userId) return null;
      return `stunnr_recent_fonts_${bpId}_u${userId}`;
    },

    loadRecentFonts() {
      const key = this.getRecentFontsKey();
      this.recentFonts = key ? get<RecentFont[]>(key, []) : [];
    },

    addRecentFont(family: string, type: FontGroup) {
      const filtered = this.recentFonts.filter(f => f.family !== family);
      this.recentFonts = [{ family, type }, ...filtered].slice(0, 10);
      const key = this.getRecentFontsKey();
      if (key) set(key, this.recentFonts);
    }
  }
});
